import { Controller } from "@hotwired/stimulus";

// Contrôleur pour les formulaires de recherche avec soumission automatique
export default class extends Controller {
  static values = { delay: { type: Number, default: 500 } }
  
  // Propriétés statiques pour persister entre les instances lors des rendus Turbo
  static lastFocusedElementId = null;
  static lastCursorPosition = null;

  connect() {
    console.log("SearchForm controller connected", this.element);
    this.timeout = null;
    this.restoreFocus();
    
    // Écouter l'événement native reset du formulaire
    this.element.addEventListener("reset", this.handleReset.bind(this));
  }

  disconnect() {
    console.log("SearchForm controller disconnected", this.element);
    clearTimeout(this.timeout);
    this.element.removeEventListener("reset", this.handleReset);
  }

  // Capture l'élément focalisé et la position du curseur avant un rendu Turbo
  captureFocus() {
    const activeElement = document.activeElement;
    // Vérifier si l'élément fait partie du formulaire (soit dedans, soit lié via l'attribut form)
    const isFormElement = this.element.contains(activeElement) || activeElement.form === this.element;

    if (isFormElement && activeElement.id) {
      this.constructor.lastFocusedElementId = activeElement.id;
      this.constructor.lastCursorPosition = activeElement.selectionStart;
    }
  }

  // Restaure le focus et la position du curseur
  restoreFocus() {
    const id = this.constructor.lastFocusedElementId;
    if (id) {
      // Chercher l'élément soit dans le formulaire, soit n'importe où dans le document (pour les modales téléportées)
      const element = this.element.querySelector(`#${id}`) || document.getElementById(id);

      if (element) {
        element.focus();

        // Restaurer la position du curseur pour les entrées de texte
        if (this.constructor.lastCursorPosition !== null && ['text', 'search', 'tel', 'url', 'password', 'email'].includes(element.type)) {
          element.setSelectionRange(this.constructor.lastCursorPosition, this.constructor.lastCursorPosition);
        }
      }

      // Réinitialiser après restauration
      this.constructor.lastFocusedElementId = null;
      this.constructor.lastCursorPosition = null;
    }
  }

  // Gérer la réinitialisation personnalisée (Clear All)
  handleReset(event) {
    // Si l'événement vient d'un bouton reset natif ou d'un appel manuel
    if (event) event.preventDefault();
    console.log("SearchForm: Custom reset (clear all) triggered");

    const elements = this.element.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      
      // On ignore les éléments de contrôle (boutons, fieldsets)
      if (el.tagName === 'BUTTON' || el.tagName === 'FIELDSET' || el.type === 'submit' || el.type === 'reset') continue;

      // Vider la valeur
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = false;
      } else {
        el.value = '';
      }

      // Déclencher un événement 'change' pour notifier Alpine, Select2, Flatpickr
      el.dispatchEvent(new Event('change', { bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Effacer tout timeout de soumission en cours et soumettre immédiatement
    clearTimeout(this.timeout);
    console.log("SearchForm: Form cleared, submitting...");
    
    // Utiliser requestSubmit pour déclencher les événements de soumission (compatibilité Turbo)
    if (typeof this.element.requestSubmit === 'function') {
      this.element.requestSubmit();
    } else {
      this.element.submit();
    }
  }

  // Soumettre le formulaire automatiquement après un délai
  submit(event) {
    if (event && event.type === 'input' && event.target.tagName === 'INPUT') {
      this.captureFocus();
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.element.requestSubmit();
      }, this.delayValue);
    } else {
      this.element.requestSubmit();
    }
  }

  // L'action reset via data-action="search-form#reset" ou data-action="click->search-form#reset"
  reset(event) {
    this.handleReset(event);
  }
}